import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const CalculatedPage = () => {
  const [templateName, setTemplateName] = useState('');
  const [fields, setFields] = useState([
    { fieldName: '', type: 'String', dropdownList: '', validationRule: '', mandatory: false },
  ]);
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/calculated`);
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      } else {
        console.error('Failed to fetch calculated templates');
      }
    } catch (error) {
      console.error('Error fetching calculated templates:', error);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/calculated/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setTemplates(templates.filter(template => template._id !== id));
      } else {
        console.error('Failed to delete calculated template');
      }
    } catch (error) {
      console.error('Error deleting calculated template:', error);
    }
  };

  const addField = () => {
    setFields([...fields, { fieldName: '', type: 'String', dropdownList: '', validationRule: '', mandatory: false }]);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index, key, value) => {
    const updatedFields = [...fields];
    updatedFields[index][key] = value;
    setFields(updatedFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const templateData = { templateName, fields };
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/calculated`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData),
      });
      if (res.ok) {
        setMessage('Calculated template saved successfully!');
        setTemplateName('');
        setFields([{ fieldName: '', type: 'String', dropdownList: '', validationRule: '', mandatory: false }]);
        fetchTemplates();
      } else {
        setMessage('Failed to save calculated template');
      }
    } catch (error) {
      console.error('Error saving calculated template:', error);
      setMessage('An error occurred while saving the template.');
    }
  };

  return (
    <div className="container">
      <h2>Calculated Template</h2>
      <p>Configure the fields for the Calculated section of the Performance Report.</p>

      {/* Existing Templates Table */}
      <h4>Existing Templates</h4>
      {templates.length > 0 ? (
        <table className="table table-bordered">
          <thead>
            <tr>
              <th style={{ width: '10%' }}>#</th>
              <th>Template Name</th>
              <th style={{ width: '10%' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((temp, index) => (
              <tr key={temp._id}>
                <td>{index + 1}</td>
                <td>{temp.templateName}</td>
                <td className="text-center">
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => deleteTemplate(temp._id)}
                    title="Delete Template"
                  >
                    X
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No templates found.</p>
      )}

      {/* Create New Template Form */}
      <h4 className="mt-4">Create New Template</h4>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="templateName" className="form-label">Template Name</label>
          <input
            type="text"
            id="templateName"
            className="form-control"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name"
            required
          />
        </div>
        <h5>Fields</h5>
        {fields.map((field, index) => (
          <div key={index} className="mb-3 border p-3">
            <div className="row align-items-end">
              <div className="col-md-3">
                <label className="form-label">Field Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={field.fieldName}
                  onChange={(e) => handleFieldChange(index, 'fieldName', e.target.value)}
                  placeholder="Enter field name"
                  required
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Type of Entry</label>
                <select
                  className="form-select"
                  value={field.type}
                  onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                >
                  <option value="String">String</option>
                  <option value="Datetime">Datetime</option>
                  <option value="Number">Number</option>
                  <option value="Decimal">Decimal</option>
                  <option value="Dropdown">Dropdown</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Validation Rule</label>
                <input
                  type="text"
                  className="form-control"
                  value={field.validationRule}
                  onChange={(e) => handleFieldChange(index, 'validationRule', e.target.value)}
                  placeholder="Enter validation rule"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Mandatory</label>
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`mandatory-${index}`}
                    checked={field.mandatory}
                    onChange={(e) => handleFieldChange(index, 'mandatory', e.target.checked)}
                  />
                </div>
              </div>
              <div className="col-md-2 text-end">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => removeField(index)}
                  title="Remove Field"
                >
                  X
                </button>
              </div>
            </div>
            {field.type === 'Dropdown' && (
              <div className="row mt-3">
                <div className="col-md-12">
                  <label className="form-label">Dropdown List (comma-separated)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={field.dropdownList}
                    onChange={(e) => handleFieldChange(index, 'dropdownList', e.target.value)}
                    placeholder="Enter dropdown options, separated by commas"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
        <div className="mb-3">
          <button type="button" className="btn btn-secondary" onClick={addField}>
            Add New Field
          </button>
        </div>
        <button type="submit" className="btn btn-primary">
          Save Template
        </button>
      </form>
      {message && <div className="mt-3 alert alert-info">{message}</div>}
    </div>
  );
};

export default CalculatedPage;
